import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AppDispatch } from "../../../app/store";
import ClickIcon from "../../../components/essentials/icon-buttons/click-icon/ClickIcon";
import UserCard, {
	UserPreviewInfo,
} from "../../../components/essentials/user-card/UserCard";

import { selectUser, setUser } from "../../../core/auth/authSlice";
import {
	addMessage,
	addNewChat,
	editMessage,
	removeMessage,
	selectMessages,
	setActiveChat,
	setMessages,
} from "../../../core/auth/chatSlice";
import { ChatApiRoutes } from "../../../shared/api-routes/chat.api-routes";
import { User } from "../../../shared/models";
import { useWebSocket } from "../../../shared/socket-provider/WebSocketProvider";
import http from "../../../shared/utils/http";
import Message, { IMessage } from "../components/message/Message";
import AcedTextBox from "./AcedTextBox";
import "./Chat.scss";

export interface ChatData {
	id: string;
	other: UserPreviewInfo;
}

const Chat: React.FC = () => {
	const { t } = useTranslation();
	const user = useSelector(selectUser) as User;
	const [other, setOther] = useState<UserPreviewInfo>();
	const [message, setMessage] = useState<string>("");

	const params = useParams();
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const location = useLocation();
	const messageState = useSelector(selectMessages);

	useEffect(() => {
		if (params.chatId) {
			// Check if this chat has an unread message before opening it
			const chat = messageState.chats.find((c) => c.id === params.chatId);
			const isUnread = chat?.lastMessage && !chat.lastMessage.read;

			dispatch(setActiveChat(params.chatId));
			http
				.get(ChatApiRoutes.GET_MESSAGES_IN_CHAT(params.chatId))
				.then((res: any) => {
					// Filter out system messages where current user is the sender (not receiver)
					const filteredMessages = (res.messages || []).filter((msg: IMessage) => {
						if (msg.type === "system") {
							return msg.receiver === user?.actorId;
						}
						return true;
					});
					dispatch(setMessages(filteredMessages));

					// Decrement unread count if this chat was unread
					if (isUnread && user?.unreadMessagesCount && user.unreadMessagesCount > 0) {
						dispatch(setUser({
							...user,
							unreadMessagesCount: user.unreadMessagesCount - 1
						}));
					}

					setTimeout(scrollToBottom, 15);
				});
		} else if (params.memberId) {
			// New chat - set empty state, don't set activeChat since we don't have a chatId yet
			dispatch(setActiveChat(null));
			dispatch(setMessages([]));
		}

		return () => {
			dispatch(setActiveChat(null));
		};
	}, [params.chatId, params.memberId]);

	const socket = useWebSocket();

	const freeSocket = () => {
		if (socket) {
			socket.emit("leave_chat", params.chatId);
			socket.off("editMessage");
		}
	};

	useEffect(() => {
		if (socket && params.chatId) {
			console.log(
				"Chat: Socket connected:",
				socket.connected,
				"chatId:",
				params.chatId,
			);
			freeSocket();
			socket.emit("join_chat", params.chatId);

			socket.on("newMessage", (newMessage) => {
				const container = containerRef.current;
				if (!container) return;

				const nearBottom =
					container.scrollHeight -
						container.scrollTop -
						container.clientHeight <
					100;

				if (nearBottom) {
					setTimeout(scrollToBottom, 0);
				}
			});

			socket.on("editMessage", (editedMessage) => {
				dispatch(editMessage(editedMessage));
			});

			socket.on("deleteMessage", (deletedMessageId) => {
				dispatch(removeMessage(deletedMessageId));
			});

			return () => {
				freeSocket();
			};
		}
	}, [params.chatId, socket]);

	useEffect(() => {
		return () => {
			freeSocket();
		};
	}, []);

	const handleSendMessage = async () => {
		if (!message?.trim()) return;

		// Handle new chat (first message)
		if (params.memberId && !params.chatId) {
			try {
				await http
					.post(ChatApiRoutes.SEND_FIRST_MESSAGE, {
						receiver: params.memberId,
						firstMessage: message,
					})
					.then((response: any) => {
						const newChatId = response.chatId;
						const firstMessage = response.message;

						// Add new chat to Redux store
						if (otherInfo && firstMessage) {
							dispatch(
								addNewChat({
									id: newChatId,
									lastMessage: firstMessage,
									other: otherInfo,
								}),
							);
						}

						setMessage("");
						// Preserve otherInfo when navigating to new chat
						navigate("/messages/" + newChatId, {
							state: { otherInfo },
						});
					});
			} catch (error) {
				console.error("Error sending first message:", error);
			}
			return;
		}

		if (params.chatId) {
			if (editing) {
				try {
					await http
						.patch(ChatApiRoutes.EDIT_MESSAGE(params.chatId, activeMessage), {
							text: message,
						})
						.then((res: IMessage) => {
							setMessage("");
						});
					setEditing(false);
				} catch (error) {
					console.error("Error sending message:", error);
				}
			} else {
				try {
					await http
						.post(ChatApiRoutes.SEND_MESSAGE(params.chatId), {
							text: message,
						})
						.then((res: IMessage) => {
							setMessage("");
							params.chatId &&
								dispatch(
									addMessage({
										chatId: params.chatId,
										message: res,
									}),
								);
							setTimeout(scrollToBottom, 15);
						});
				} catch (error) {
					console.error("Error sending message:", error);
				}
			}
		}
	};

	const containerRef = useRef<HTMLDivElement | null>(null);

	const [scrollTop, setScrollTop] = useState(0);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const handleScroll = () => {
			setScrollTop(container.scrollTop);
		};

		container.addEventListener("scroll", handleScroll);
		return () => container.removeEventListener("scroll", handleScroll);
	}, []);

	const scrollToBottom = () => {
		const container = containerRef.current;
		if (container) {
			container.scrollTop = container.scrollHeight;
		}
	};

	const inputRef = useRef<HTMLTextAreaElement>(null);
	const [editing, setEditing] = useState<boolean>(false);
	const [activeMessage, setActiveMessage] = useState<string | undefined>(
		undefined,
	);

	const startEditing = (messageId: string, text: string) => {
		setActiveMessage(messageId);
		setMessage(text);
		setEditing(true);
		inputRef.current?.focus();
	};

	useEffect(() => {
		if (editing) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "auto";
		}

		return () => {
			document.body.style.overflow = "auto";
		};
	}, [editing]);

	const onDeleteMessage = (deletedId: string) => {
		if (messageState.activeChat.chatId)
			http
				.delete(
					ChatApiRoutes.DELETE_MESSAGE(
						messageState.activeChat.chatId,
						deletedId,
					),
				)
				.then((res) => {
					params.chatId && dispatch(removeMessage(deletedId));
				});
	};

	const onCancelEdit = () => {
		setEditing(false);
		setMessage("");
	};

	const [otherInfo, setOtherInfo] = useState<UserPreviewInfo | undefined>(
		location.state?.otherInfo,
	);
	useEffect(() => {
		if (params.chatId) {
			// First check if otherInfo was passed via navigation state
			if (location.state?.otherInfo) {
				setOtherInfo(location.state.otherInfo);
			} else {
				setOtherInfo(
					messageState.chats.find((chat) => chat.id === params.chatId)?.other,
				);
			}
		} else if (params.memberId) {
			// Fetch member info for new chat
			http.get(`/members/${params.memberId}`).then((res: UserPreviewInfo) => {
				setOtherInfo(res);
			});
		}
	}, [messageState, params.chatId, params.memberId]);

	const [width, setWidth] = useState(window.innerWidth);

	useEffect(() => {
		const handleResize = () => setWidth(window.innerWidth);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return (
		<div className="chat__container">
			<div className="chat__header">
				<ClickIcon
					icon={`${width < 768 ? "backarrow" : "close"}`}
					color="black"
					onClick={() => {
						dispatch(setActiveChat(null));
						navigate("/messages");
					}}
				></ClickIcon>

				<UserCard
					userInfo={otherInfo}
					darkMode
				/>
			</div>

			<div
				ref={containerRef}
				className={`chat__timeline-container ${
					editing ? "chat__timeline-container--no-scroll" : ""
				}`}
			>
				{editing && (
					<div
						className="chat__overlay"
						style={{
							top: scrollTop,
						}}
					>
						<div
							className="chat__overlay-bg"
							onClick={onCancelEdit}
						></div>
						<div className="chat__overlay-tooltip">
							<p>{t("messages.editingMessage")}</p>
							<ClickIcon
								icon="close"
								onClick={onCancelEdit}
								color="black"
							/>
						</div>
					</div>
				)}

				<div className="chat__timeline">
					{messageState.activeChat.messages.map((mess, index) => (
						<Message
							message={mess}
							other={otherInfo}
							key={mess.id}
							startEditing={startEditing}
							onDelete={onDeleteMessage}
							hasName={(() => {
								if (
									index === messageState.activeChat.messages.length - 1 ||
									messageState.activeChat.messages[index + 1].sender !==
										mess.sender
								) {
									return true;
								} else {
									const nextMess = messageState.activeChat.messages[index + 1];
									const timestamp1 = new Date(mess.createdAt);
									const timestamp2 = new Date(nextMess.createdAt); // or another timestamp

									const diffInMs = Math.abs(
										timestamp2.getTime() - timestamp1.getTime(),
									);
									const fiveMinutesInMs = 5 * 60 * 1000;

									return diffInMs > fiveMinutesInMs;
								}
							})()}
							scrollAction={scrollToBottom}
						/>
					))}
				</div>
			</div>

			<div className="chat__footer">
				<AcedTextBox
					ref={inputRef}
					text={message}
					setText={setMessage}
					onSend={handleSendMessage}
					placeholder={t("messages.inputPlaceholder")}
					maxRows={5}
					mode="light"
				/>
			</div>
		</div>
	);
};

export default Chat;
